import React from "react";
import {
  styled,
  Button,
  Typography,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import WarningIcon from "@icons/myicons/warning-circle-filled.svg?react";

const Container = styled("div")`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const HeaderTab = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const MissingDividendHeader = styled("div")`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const MissingTextContainer = styled("div")`
  display: flex;
  align-items: center;

  ${({ theme }) => theme.breakpoints.down("md")} {
    align-items: flex-start;
  }
`;

const MissingDividendsIcon = styled(WarningIcon)`
  color: ${({ theme }) => theme.palette.warning.main};
  margin-right: ${({ theme }) => theme.spacing(1)};
  margin-left: ${({ theme }) => theme.spacing(-1)};
`;

const MobileActionsRow = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

type Props = {
  unreceivedDividendsCount: number;
  investmentId: number;
  splitAdjust: boolean;
  onToggleSplitAdjust: () => void;
};

export const DividendsTableHeader = ({
  unreceivedDividendsCount,
  investmentId,
  splitAdjust,
  onToggleSplitAdjust,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMd = useMediaQuery(theme.breakpoints.down("md"));

  const renderMissingDividendsHeader = () => {
    if (unreceivedDividendsCount <= 0) {
      return null;
    }

    return (
      <MissingDividendHeader data-testid="missing-dividends-container">
        <MissingTextContainer>
          <MissingDividendsIcon />
          <Typography variant="body1">
            {t("investment.dividends.missingDividends.header", {
              count: unreceivedDividendsCount,
              value: unreceivedDividendsCount,
            })}
          </Typography>
        </MissingTextContainer>
        <Button
          variant="text"
          color="primary"
          size="small"
          component={Link}
          to={`/investments/${investmentId}/dividend-wizard/create/batch`}
        >
          {t("investment.dividends.missingDividends.addBatchButton")}
        </Button>
      </MissingDividendHeader>
    );
  };

  const renderAddDividendsButton = () => (
    <Button
      variant="primary"
      size="small"
      component={Link}
      to={`/investments/${investmentId}/dividend-wizard/create`}
    >
      {t("investment.dividends.addDividends")}
    </Button>
  );

  const renderSplitAdjustSwitch = () => (
    <FormControlLabel
      control={
        <Switch
          checked={splitAdjust}
          value={splitAdjust}
          onChange={onToggleSplitAdjust}
        />
      }
      label={t("investment.dividends.splitAdjust")}
      sx={{
        margin: theme.spacing(0),
      }}
    />
  );

  if (isMd || unreceivedDividendsCount <= 0) {
    return (
      <Container>
        {renderMissingDividendsHeader()}
        <MobileActionsRow>
          {renderSplitAdjustSwitch()}
          {renderAddDividendsButton()}
        </MobileActionsRow>
      </Container>
    );
  }

  return (
    <Container>
      <HeaderTab>
        {renderMissingDividendsHeader()}
        {renderAddDividendsButton()}
      </HeaderTab>
      {renderSplitAdjustSwitch()}
    </Container>
  );
};
